import { Disease, FunctionalStatus } from "@domain/medical-condition/medical-condition";
import {
	isPendingPatient,
	Patient,
	PatientGlobalComplianceListItem,
	PatientList,
	PatientNameListItem,
	PendingPatient,
} from "@domain/patient/patient";
import { PatientService, PendingPatientCreateDto } from "@domain/patient/patient-service";
import { isNotNil } from "@utils/assert";
import { Map } from "immutable";
import { Observable, observable } from "micro-observables";
import { isApiError } from "@core/api/api-error";
import { PatientErrorCodes, PatientNotPendingError, PendingPatientForbiddenAccessError } from "@domain/patient/patient-errors";

export class PatientManager {
	private readonly _allPatients = observable<PatientList | null>(null);
	private readonly _registeredPatientCompliances = observable<PatientGlobalComplianceListItem[]>([]);
	private readonly _patientMap = observable<Map<string, Patient | PendingPatient>>(Map());

	allPatients: Observable<PatientList>;
	patientMap = this._patientMap.readOnly();

	constructor(private readonly patientService: PatientService) {
		this.allPatients = Observable.select(
			[this._allPatients, this._registeredPatientCompliances],
			(currentPatients, currentCompliances) => ({
				pendingPatients: currentPatients?.pendingPatients ?? [],
				registeredPatients:
					currentPatients?.registeredPatients.map(registeredPatient => ({
						...registeredPatient,
						compliance: currentCompliances.find(it => it.id === registeredPatient.id)?.globalCompliance,
					})) ?? [],
			}),
		);
	}

	async createPendingPatient(pendingPatientPutDto: PendingPatientCreateDto): Promise<PendingPatient> {
		const newPendingPatient = await this.patientService.createPendingPatient(pendingPatientPutDto);
		this.addPendingPatient(newPendingPatient);
		return newPendingPatient;
	}

	async retrievePatient(patientId: string): Promise<void> {
		try {
			const newPatient = await this.patientService.retrievePatient(patientId);
			this._patientMap.update(patientMap => patientMap.set(newPatient.id, newPatient));
		} catch (e) {
			if (isApiError(e) && e.code === PatientErrorCodes.PendingPatientForbiddenAccess) {
				throw new PendingPatientForbiddenAccessError();
			}
			throw e;
		}
	}

	async deletePatient(patientId: string): Promise<void> {
		await this.patientService.deletePatient(patientId);
		this.removePatient(patientId);
	}

	async deletePendingPatient(patientId: string): Promise<void> {
		await this.patientService.deletePendingPatient(patientId);
		this.removePendingPatient(patientId);
	}

	async editPatientDetails(
		patientId: string,
		disease?: Disease,
		functionalStatus?: FunctionalStatus,
	): Promise<void> {
		await this.patientService.editPatientDetails(patientId, disease, functionalStatus);
		const patientToUpdate = this._patientMap.get().get(patientId);
		if (isNotNil(patientToUpdate)) {
			patientToUpdate.functionalStatus = functionalStatus;
			if (!isPendingPatient(patientToUpdate)) {
				patientToUpdate.disease = disease;
			}
			this._patientMap.update(patientMap => patientMap.set(patientToUpdate.id, patientToUpdate));
		}
	}

	async fetchAllPatients(): Promise<void> {
		const [pendingPatients, registeredPatients] = await Promise.all([
			this.patientService.listPendingPatients(),
			this.patientService.listRegisteredPatient(),
		]);

		this._allPatients.set({ registeredPatients, pendingPatients });
	}

	async listRegisteredPatientNames(): Promise<PatientNameListItem[]> {
		return this.patientService.listRegisteredPatientNames();
	}

	async fetchRegisteredPatientGlobalCompliances(): Promise<void> {
		const compliances = await this.patientService.listRegisteredPatientGlobalCompliances();
		this._registeredPatientCompliances.set(compliances);
	}

	async sendInvitationCodeEmail(patientId: string, email: string): Promise<void> {
		try {
			await this.patientService.sendInvitationCodeEmail(patientId, email);
		} catch (e) {
			if (isApiError(e) && e.status === 400) {
				throw new PatientNotPendingError();
			}
			throw e;
		}
		this._allPatients.update(currentPatients => ({
			pendingPatients:
				currentPatients?.pendingPatients.map(pendingPatient =>
					pendingPatient.id === patientId ? { ...pendingPatient, email } : pendingPatient,
				) ?? [],
			registeredPatients: currentPatients?.registeredPatients ?? [],
		}));
	}

	async reset(): Promise<void> {
		this._allPatients.set(null);
		this._patientMap.set(Map());
	}

	private addPendingPatient(pendingPatient: PendingPatient) {
		this._allPatients.update(previousPatients => ({
			pendingPatients: [pendingPatient, ...(previousPatients?.pendingPatients ?? [])],
			registeredPatients: previousPatients?.registeredPatients ?? [],
		}));
	}

	private removePendingPatient(patientId: string) {
		this._allPatients.update(previousPatients => ({
			pendingPatients: [...(previousPatients?.pendingPatients.filter(patient => patient.id !== patientId) ?? [])],
			registeredPatients: previousPatients?.registeredPatients ?? [],
		}));
		this._patientMap.update(previousMap => previousMap.filter(patient => patient.id !== patientId));
	}

	private removePatient(patientId: string) {
		this._allPatients.update(previousPatients => ({
			pendingPatients: previousPatients?.pendingPatients ?? [],
			registeredPatients: [...(previousPatients?.registeredPatients.filter(patient => patient.id !== patientId) ?? [])],
		}));
		this._patientMap.update(previousMap => previousMap.filter(patient => patient.id !== patientId));
	}
}
